import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice, type Product } from "@/hooks/useEntitlements";

interface Props {
  product: Product | null;
  open: boolean;
  onClose: () => void;
}

const maskDoc = (v: string) => v.replace(/\D/g, "").slice(0, 14);

export default function CheckoutDialog({ product, open, onClose }: Props) {
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [installments, setInstallments] = useState(false);

  const hasInstallments = !!(product?.installment_count && product?.installment_price_cents);
  const installmentTotal =
    hasInstallments ? product!.installment_count! * product!.installment_price_cents! : 0;

  // Pre-fill name and document from the saved profile
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("nome, cpf_cnpj")
        .eq("id", uid)
        .maybeSingle();
      if (cancelled || !profile) return;
      setName((prev) => prev || profile.nome || "");
      setCpfCnpj((prev) => prev || maskDoc(profile.cpf_cnpj ?? ""));
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);


  const handleSubmit = async () => {
    if (!product) return;
    const doc = maskDoc(cpfCnpj);
    if (doc.length !== 11 && doc.length !== 14) {
      toast.error("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-asaas-charge", {
        body: { productKey: product.key, cpfCnpj: doc, name, installments: installments && hasInstallments },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.checkoutUrl) throw new Error("Não foi possível abrir o pagamento.");
      setCheckoutUrl(data.checkoutUrl);
      window.open(data.checkoutUrl, "_blank", "noopener");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao iniciar o pagamento.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setCheckoutUrl(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{product?.name}</DialogTitle>
          <DialogDescription>
            {product ? (
              <>
                {formatPrice(product.price_cents)} à vista
                {hasInstallments
                  ? ` ou ${product.installment_count}x de ${formatPrice(product.installment_price_cents!)}`
                  : ""}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {checkoutUrl ? (
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Abrimos a página de pagamento em outra aba. Assim que o pagamento for confirmado, seu
              acesso é liberado automaticamente aqui — você não precisa fazer nada.
            </p>
            <Button variant="outline" className="w-full" onClick={() => window.open(checkoutUrl, "_blank", "noopener")}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Reabrir pagamento
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{product?.description}</p>
            {hasInstallments && (
              <div className="space-y-2">
                <Label>Forma de pagamento</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setInstallments(false)}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      installments ? "border-border" : "border-primary bg-primary/5"
                    }`}
                  >
                    <span className="block text-sm font-medium">À vista</span>
                    <span className="block text-xs text-muted-foreground">
                      {formatPrice(product!.price_cents)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInstallments(true)}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      installments ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <span className="block text-sm font-medium">
                      {product!.installment_count}x de {formatPrice(product!.installment_price_cents!)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Total {formatPrice(installmentTotal)}
                    </span>
                  </button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="checkout-name">Nome completo</Label>
              <Input
                id="checkout-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Como está no documento"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checkout-doc">CPF ou CNPJ</Label>
              <Input
                id="checkout-doc"
                inputMode="numeric"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(maskDoc(e.target.value))}
                placeholder="Somente números"
              />
              <p className="text-xs text-muted-foreground">
                Necessário para emitir a cobrança (Pix, cartão ou boleto).
              </p>
            </div>
            <Button className="w-full" onClick={handleSubmit} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Ir para o pagamento
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
